import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { formatApiError } from "../utils/formatApiError";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyMagicLinkPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const verifyAttempted = useRef(false);

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent double verification
      if (verifyAttempted.current) return;
      verifyAttempted.current = true;

      const token = searchParams.get("token");
      const redirectTo = searchParams.get("redirect");

      if (!token) {
        setStatus("error");
        toast.error("Invalid or missing token");
        return;
      }

      try {
        const response = await api.post(`${API}/auth/verify-magic-link`, { token });
        localStorage.setItem("user_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        // The `storage` event AuthContext listens to only fires in other tabs.
        // Without an explicit refresh the Header stays logged-out until a hard
        // reload remounts AuthProvider.
        await refresh();

        setStatus("success");
        toast.success("Logged in successfully!");

        // Check if profile is complete
        const isProfileComplete = response.data.is_profile_complete;

        setTimeout(() => {
          if (!isProfileComplete) {
            navigate("/complete-profile");
          } else if (redirectTo) {
            navigate(redirectTo);
          } else {
            navigate("/");
          }
        }, 1500);
      } catch (error) {
        setStatus("error");
        toast.error(formatApiError(error, "Invalid or expired link"));
      }
    };

    verifyToken();
  }, [searchParams, navigate, refresh]);

  return (
    <div>
      <SEO title="Verifying Login" description="" path="/auth/verify" noindex />
      <Header pageTitle="Verifying Login" />

      <div className="container" style={{ maxWidth: "768px" }}>
        <div style={{ maxWidth: "500px", margin: "0 auto" }}>
          <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
            {status === "verifying" && (
              <>
                <Loader2 size={48} color="#1B4D3E" style={{ margin: "0 auto 1rem", animation: "spin 1s linear infinite" }} />
                <h2 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", color: "#1B4D3E" }}>
                  Verifying your login...
                </h2>
                <p style={{ color: "#666660", fontSize: "0.9rem" }}>Please wait a moment.</p>
              </>
            )}

            {status === "success" && (
              <>
                <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✓</div>
                <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#2E7D32" }}>
                  Login Successful!
                </h2>
                <p style={{ color: "#666660" }}>Redirecting you now...</p>
              </>
            )}

            {status === "error" && (
              <>
                <div style={{ fontSize: "4rem", marginBottom: "1rem", color: "#D32F2F" }}>✗</div>
                <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#D32F2F" }}>
                  Login Failed
                </h2>
                <p style={{ color: "#666660", marginBottom: "1.5rem" }}>
                  This link is invalid or has expired.
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="btn btn-primary"
                >
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyMagicLinkPage;
